import { FIELD_TYPES, queryView, type CollectionData, type FieldDef, type FilterOp, type RecordData, type ViewDef, type ViewType } from '@tomo/core';
import { useMemo } from 'react';
import { useProject, useProjectValue, useRecords } from '../../data/hooks.tsx';

export const VIEW_TYPE_LABEL: Record<ViewType, string> = { table: 'Table', board: 'Board', gallery: 'Gallery', list: 'List' };

/** What one record of a collection is called in buttons and placeholders ("unit", "doc", "task"…). */
export function itemNoun(c: Pick<CollectionData, 'titleLabel'>): string {
  return c.titleLabel.toLowerCase();
}

export const capitalize = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

/**
 * Labels for "New …" menus: just "Civilization", not "Civilization in Civilizations". Collections
 * that share a noun (say, two kinds of doc) also get their name as a hint to tell them apart.
 */
export function newRecordLabels(collections: CollectionData[]): { noun: string; hint?: string }[] {
  const nouns = collections.map((c) => itemNoun(c));
  return collections.map((c, i) => ({ noun: nouns[i]!, hint: nouns.indexOf(nouns[i]!) !== nouns.lastIndexOf(nouns[i]!) ? c.name : undefined }));
}

/** Records of a view after search, filters and sort. Recomputes when records, the view or any
 * referenced record (relations/people names) changes. */
export function useViewRecords(collection: CollectionData, view: ViewDef, search: string): RecordData[] {
  const project = useProject();
  const records = useRecords(collection.id);
  // Relation titles and people names participate in search/sort, so depend on the project version
  // only when the view actually uses such values.
  const usesRefs = view.sort.length > 0 || view.filters.length > 0 || search.trim().length > 0;
  const version = useProjectValue((p) => (usesRefs ? p.version : 0));
  return useMemo(
    () => queryView(records, collection, view, search, { ...project.valueContext(), fields: collection.fields }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [records, collection, view, search, project, version],
  );
}

export const DEFAULT_WIDTH: Record<FieldDef['type'], number> = {
  text: 220,
  number: 130,
  select: 150,
  multiSelect: 210,
  checkbox: 100,
  date: 140,
  person: 170,
  relation: 230,
  url: 180,
  rating: 130,
  range: 130,
  dice: 140,
  color: 130,
  formula: 120,
  votes: 110,
  file: 220,
};

export const TITLE_WIDTH = 260;

export function fieldWidth(view: ViewDef, field: FieldDef): number {
  return view.fieldWidths[field.id] ?? (field.type === 'number' && field.config.display === 'bar' ? 190 : DEFAULT_WIDTH[field.type]);
}

export function visibleFields(collection: CollectionData, view: ViewDef): FieldDef[] {
  const hidden = new Set(view.hiddenFields);
  return collection.fields.filter((f) => !hidden.has(f.id));
}

export const OP_LABEL: Record<FilterOp, string> = {
  contains: 'contains',
  notContains: 'does not contain',
  is: 'is',
  isNot: 'is not',
  isEmpty: 'is empty',
  isNotEmpty: 'is not empty',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  hasAny: 'has any of',
  hasAll: 'has all of',
  hasNone: 'has none of',
  checked: 'is checked',
  unchecked: 'is unchecked',
  before: 'is before',
  after: 'is after',
};

export const NO_VALUE_OPS = new Set<FilterOp>(['isEmpty', 'isNotEmpty', 'checked', 'unchecked']);

export function opsFor(field: FieldDef | undefined): FilterOp[] {
  if (!field) return ['contains', 'notContains', 'is', 'isNot', 'isEmpty', 'isNotEmpty'];
  return FIELD_TYPES[field.type].filterOps;
}

/** Groupable fields for boards. */
export function groupableFields(collection: CollectionData): FieldDef[] {
  return collection.fields.filter((f) => FIELD_TYPES[f.type].groupable);
}
